import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { buildDrillDownState, ROUTES, useIsComponentCropped } from '@cyberint/common-utils';
import { CustomTooltip, Tag, TagVariants, Images, RemoteImage, Themes } from '@cyberint/dsc';
import * as S from './styled';

export const EntityType = {
  THREAT_ACTOR: 'threat-actor',
  MALWARE: 'malware',
  CAMPAIGN: 'campaign'
};

const getEntityFallbackImage = entityType =>
  ({
    [EntityType.THREAT_ACTOR]: Images.DefaultMalware,
    [EntityType.MALWARE]: Images.DefaultMalware,
    [EntityType.CAMPAIGN]: Images.CampaignImage
  }[entityType]);

const getEntityPathname = (entityType, id) =>
  ({
    [EntityType.THREAT_ACTOR]: `/${ROUTES.THREAT_ACTORS_TAB}/${ROUTES.CARD}/${id}`,
    [EntityType.MALWARE]: `/${ROUTES.MALWARE_KB_TAB}/${ROUTES.CARD}/${id}`,
    [EntityType.CAMPAIGN]: `/${ROUTES.CAMPAIGNS_TAB}/${id}`
  }[entityType]);

export const EntityTag = ({
  entity,
  type,
  onClickHandler,
  hasTooltipArrow,
  mode,
  testId,
  tooltip,
  variant = TagVariants.MAIN
}) => {
  const navigate = useNavigate();
  const { componentRef, isCropped } = useIsComponentCropped(entity.name);

  const handleClick = () => {
    const pathname = getEntityPathname(type, entity.id);
    navigate(pathname, { state: buildDrillDownState() });
  };

  const content = (
    <>
      <RemoteImage
        remoteSrc={entity.profile_picture || getEntityFallbackImage(type)}
        isCircular
        width={16}
        height={16}
        alt={entity.name}
      />
      <S.Name ref={componentRef}>{entity.name}</S.Name>
    </>
  );

  return (
    <S.TagWrapper>
      <CustomTooltip
        arrow={hasTooltipArrow}
        mode={Themes.LIGHT}
        disableHoverListener={tooltip ? false : !isCropped}
        title={tooltip || entity.name}>
        <div>
          <Tag
            className={mode}
            testId={testId}
            onClick={onClickHandler || handleClick}
            clickable
            variant={variant}
            content={content}
            maxWidth="250px"
          />
        </div>
      </CustomTooltip>
    </S.TagWrapper>
  );
};

EntityTag.propTypes = {
  type: PropTypes.oneOf(Object.values(EntityType)),
  entity: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    profile_picture: PropTypes.oneOfType([PropTypes.string, null])
  }).isRequired,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node, null]),
  variant: PropTypes.string,
  testId: PropTypes.string,
  mode: PropTypes.string,
  hasTooltipArrow: PropTypes.bool,
  onClickHandler: PropTypes.func
};
